import React, { FC, Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PageTitle from 'components/shared/page-title/page-title.component';
import ChipInput from 'components/shared/chip-input/chip-input.component';
import { emailRegExp } from 'config/regex';
import {
    Box,
    Button,
    FormControl,
    FormControlLabel,
    Radio,
    RadioGroup,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { updateNotificationTemplate } from 'apollo/templates/user/mutation.graphql';
import { useMutation } from '@apollo/client';
import AlertSnackbar from 'components/shared/alert-snackbar/alert-snackbar.component';

const NotificationSettings: FC = () => {
    const { t: translate } = useTranslation(['user-settings', 'common', 'invoices']);
    const currentUser = currentUserVar();
    const [isEmailFieldValid, setIsEmailFieldValid] = useState(true);
    const [changesSaved, setChangesSaved] = useState(false);
    const [emailNotifications, setEmailNotifications] = useState({
        type: '',
        otherEmail: [] as string[],
        newMessage: false,
        orderReceivedByLab: false,
        orderDeclinedByLab: true,
        orderSentByLab: false,
    });

    const [generalNotifications, setGeneralNotifications] = useState({
        newMessage: false,
        orderReceivedByLab: false,
        orderDeclinedByLab: true,
        orderSentByLab: false,
        orderNotReceived: {
            active: false,
            days: 0,
        },
    });

    useEffect(() => {
        const otherEmail = currentUser?.emailNotifications?.otherEmail;
        setGeneralNotifications({
            ...generalNotifications,
            newMessage: currentUser?.generalNotifications?.newMessage as boolean,
            orderReceivedByLab: currentUser?.generalNotifications?.orderReceivedByLab as boolean,
            orderDeclinedByLab: currentUser?.generalNotifications?.orderDeclinedByLab ?? true,
            orderSentByLab: currentUser?.generalNotifications?.orderSentByLab as boolean,
            orderNotReceived: {
                active: currentUser?.generalNotifications?.orderNotReceived.active as boolean,
                days: currentUser?.generalNotifications?.orderNotReceived.days || 0,
            },
        });
        setEmailNotifications({
            ...emailNotifications,
            type: currentUser?.emailNotifications?.type || '',
            otherEmail: otherEmail ? (otherEmail.split(',') as []) : [],
            newMessage: currentUser?.emailNotifications?.newMessage as boolean,
            orderReceivedByLab: currentUser?.emailNotifications?.orderReceivedByLab as boolean,
            orderDeclinedByLab: currentUser?.emailNotifications?.orderDeclinedByLab ?? true,
            orderSentByLab: currentUser?.emailNotifications?.orderSentByLab as boolean,
        });
    }, [currentUser]);

    const [updateNotificationSettings, { error }] = useMutation(updateNotificationTemplate, {
        onCompleted: ({ updateNotificationSettings }) => {
            currentUser.emailNotifications = updateNotificationSettings.emailNotifications;
            currentUser.generalNotifications = updateNotificationSettings.generalNotifications;
            currentUserVar(currentUser);
            setChangesSaved(true);
        },
    });

    const handleEmailNotifications = (event: React.ChangeEvent<HTMLInputElement>) => {
        setEmailNotifications({
            ...emailNotifications,
            [event.target.name]: event.target.checked,
        });
    };
    const validateEmail = (value: string) => {
        return emailRegExp.test(String(value).toLowerCase());
    };
    const handleToError = (err: Error | null) => {
        setIsEmailFieldValid(!err);
    };

    const handleGeneralNotifications = (event: React.ChangeEvent<HTMLInputElement>) => {
        setGeneralNotifications({
            ...generalNotifications,
            [event.target.name]: event.target.checked,
        });
    };

    const handleOrderNotReceivedDaysChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setGeneralNotifications({
            ...generalNotifications,
            orderNotReceived: {
                ...generalNotifications.orderNotReceived,
                days: Number(event.target.value),
            },
        });
    };
    const handleOrderNotReceivedForActive = (event: React.ChangeEvent<HTMLInputElement>) => {
        setGeneralNotifications({
            ...generalNotifications,
            orderNotReceived: {
                ...generalNotifications.orderNotReceived,
                active: event.target.checked,
            },
        });
    };

    const handleEmailInput = (name: string, value: string[]) => {
        setEmailNotifications({
            ...emailNotifications,
            [name]: value,
        });
    };

    const handleEmailInputMethod = (event: React.ChangeEvent<HTMLInputElement>) => {
        setEmailNotifications({
            ...emailNotifications,
            type: event.target.value,
        });
    };

    const handleSave = async () => {
        const notifications = JSON.parse(
            JSON.stringify({
                emailNotificationsInput: emailNotifications,
                generalNotificationsInput: generalNotifications,
            }),
        );

        if (
            !notifications.emailNotificationsInput.otherEmail ||
            (notifications.emailNotificationsInput.otherEmail.length === 1 &&
                notifications.emailNotificationsInput.otherEmail[0] === '')
        ) {
            notifications.emailNotificationsInput.otherEmail = null;
        } else {
            notifications.emailNotificationsInput.otherEmail =
                notifications.emailNotificationsInput.otherEmail.toString();
        }

        await updateNotificationSettings({
            variables: { notificationSettings: notifications },
        });
    };

    const disableSave = () => {
        return emailNotifications.type === 'other' && !isEmailFieldValid;
    };

    return (
        <Fragment>
            {error ? <AlertSnackbar type="error" message={error?.message} /> : undefined}
            {changesSaved ? <AlertSnackbar type="success" message={translate('common:CHANGES_SAVED')} /> : undefined}
            <PageTitle headingStyle="h2" title={translate('user-settings:NOTIFICATIONS')} />
            <Box mb={4}>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>{translate('user-settings:NOTIFICATION')}</TableCell>
                                <TableCell>{translate('user-settings:IN_APPLICATION')}</TableCell>
                                <TableCell>{translate('common:EMAIL')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <TableRow>
                                <TableCell>{translate('user-settings:MESSAGE_RECEIVED')}</TableCell>
                                <TableCell>
                                    <Switch
                                        name="newMessage"
                                        checked={generalNotifications.newMessage}
                                        onChange={handleGeneralNotifications}
                                    />
                                </TableCell>
                                <TableCell>
                                    <Switch
                                        name="newMessage"
                                        checked={emailNotifications.newMessage}
                                        onChange={handleEmailNotifications}
                                    />
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>{translate('user-settings:ORDER_RECEIVED_BY_LAB')}</TableCell>
                                <TableCell>
                                    <Switch
                                        name="orderReceivedByLab"
                                        checked={generalNotifications.orderReceivedByLab}
                                        onChange={handleGeneralNotifications}
                                    />
                                </TableCell>
                                <TableCell>
                                    <Switch
                                        name="orderReceivedByLab"
                                        checked={emailNotifications.orderReceivedByLab}
                                        onChange={handleEmailNotifications}
                                    />
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>{translate('user-settings:ORDER_DECLINED_BY_LAB')}</TableCell>
                                <TableCell>
                                    <Switch
                                        name="orderDeclinedByLab"
                                        checked={generalNotifications.orderDeclinedByLab}
                                        onChange={handleGeneralNotifications}
                                    />
                                </TableCell>
                                <TableCell>
                                    <Switch
                                        name="orderDeclinedByLab"
                                        checked={emailNotifications.orderDeclinedByLab}
                                        onChange={handleEmailNotifications}
                                    />
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>{translate('user-settings:ORDER_SENT_BY_LAB')}</TableCell>
                                <TableCell>
                                    <Switch
                                        name="orderSentByLab"
                                        checked={generalNotifications.orderSentByLab}
                                        onChange={handleGeneralNotifications}
                                    />
                                </TableCell>
                                <TableCell>
                                    <Switch
                                        name="orderSentByLab"
                                        checked={emailNotifications.orderSentByLab}
                                        onChange={handleEmailNotifications}
                                    />
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>
                                    <Box display="flex" alignItems="center">
                                        <Box>{translate('user-settings:ORDER_NOT_RECIEVED')}</Box>
                                        <TextField
                                            variant="standard"
                                            hiddenLabel
                                            size="small"
                                            sx={{ maxWidth: '25px', margin: '0 10px' }}
                                            value={generalNotifications.orderNotReceived.days}
                                            onChange={handleOrderNotReceivedDaysChange}
                                            disabled={!generalNotifications?.orderNotReceived?.active}
                                        />
                                        <Box>{translate('user-settings:DAYS')}</Box>
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    <Switch
                                        name="active"
                                        checked={generalNotifications?.orderNotReceived?.active}
                                        onChange={handleOrderNotReceivedForActive}
                                    />
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            <Box mb={4}>
                <Typography variant="h3">{translate('user-settings:EMAIL_FOR_NOTIFICATIONS')}</Typography>
                <FormControl>
                    <RadioGroup value={emailNotifications.type} onChange={handleEmailInputMethod}>
                        <FormControlLabel value="user" control={<Radio />} label={currentUser?.email} />
                        <FormControlLabel
                            value="other"
                            control={<Radio />}
                            label={
                                <Fragment>
                                    <ChipInput
                                        required={emailNotifications.type === 'other'}
                                        label={translate('user-settings:EMAIL_ADDRESSES')}
                                        placeholder={translate('invoices:EMAIL_PLACEHOLDER')}
                                        createChipOnBlur
                                        options={[]}
                                        chipValidator={validateEmail}
                                        onError={handleToError}
                                        defaultValue={emailNotifications.otherEmail || []}
                                        onValueChange={(value: string[]) => handleEmailInput('otherEmail', value)}
                                        onDelete={(value: string[]) => handleEmailInput('otherEmail', value)}
                                    />
                                </Fragment>
                            }
                        />
                    </RadioGroup>
                </FormControl>
            </Box>

            <Box display="flex" justifyContent="center">
                <Button onClick={handleSave} variant="contained" disabled={disableSave()}>
                    {translate('user-settings:SAVE_CHANGES')}
                </Button>
            </Box>
        </Fragment>
    );
};

export default NotificationSettings;
